import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { IRequestList } from './types/IRequestList';
import qs from 'qs';
import { IResponseList } from './types/IResponse';

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_V1_VERSION}`,
  headers: { Accept: '*/*', 'content-type': 'application/json' },
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = (getState() as RootState).auth.token;
    if (token) {
      // headers.set('authentication', `Bearer ${token}`);
      headers.set('Authorization', `${token}`);
    }
    return headers;
  },
  credentials: 'omit'
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

// Create our baseQuery instance
const baseMicroQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_V1_VERSION}/micro`,
  headers: { Accept: '*/*', 'Content-type': 'application/json' },
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = (getState() as RootState).auth.token;
    if (token) {
      // headers.set('authentication', `Bearer ${token}`);
      headers.set('Authorization', `${token}`);
    }
    return headers;
  },
  credentials: 'omit'
});

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const apiV1 = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'icwApi',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseQueryWithRetry,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: ['Auth', 'Inspection'],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({})
});

export const microApi = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */

  reducerPath: 'icwMicroApi',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseMicroQuery,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [
    'Auth',
    'Inspection',
    'Template',
    'Inspectors',
    'Inspection-Types'
  ],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: (build) => ({
    getList: build.query<IResponseList, IRequestList>({
      query: (params: IRequestList) => {
        const { resource, current, pageSize, ...rest } = params;
        const payload = {
          pageNo: current,
          pageSize: pageSize,
          ...rest
        };
        const queryString = qs.stringify(payload, {
          encodeValuesOnly: false,
          indices: false
        });
        return {
          url: `/${resource}${queryString ? '?' + queryString : ''}`,
          method: 'GET'
        };
      },
      transformResponse(baseQueryReturnValue: IResponseList) {
        return {
          code: baseQueryReturnValue.code,
          message: baseQueryReturnValue.message,
          data: baseQueryReturnValue.data
          //TODO: Add pagination
          // pagination: baseQueryReturnValue.pagination
        };
      }
    })
  })
});

export const shareApi = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'icwUploadApi',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: fetchBaseQuery({
    // mode: 'no-cors',
    baseUrl: `${process.env.REACT_APP_BASE_URL}`,
    headers: { Accept: '*/*, *.*' },
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token = (getState() as RootState).auth.token;

      if (token) {
        headers.set('Authorization', `${token}`);
      }
      return headers;
    }
  }),
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({})
});
export const enhancedApi = apiV1.enhanceEndpoints({
  endpoints: () => ({})
});
